<template>
  <div v-if="product">
    <section class="hero-slab">
      <div class="hero-slab__media" :style="{ backgroundImage: 'url(' + product.heroImage + ')' }"></div>
      <div class="hero-slab__overlay"></div>
      <div class="hero-slab__content container reveal">
        <div class="section-title">
          <p>{{ product.eyebrow }}</p>
          <h1>{{ product.title }}</h1>
        </div>
        <p>{{ product.intro }}</p>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.products'), to: '/products' },
        { label: product.title, to: $route.path }
      ]"
      :back-label="$t('common.backToProducts')"
      back-to="/products"
    />

    <section class="section-spacing light-panel">
      <div class="container grid-two reveal">
        <div class="section-title">
          <p>Why this format</p>
          <h2>What it brings into a room</h2>
        </div>
        <div>
          <ul class="detail-list">
            <li v-for="item in product.highlights" :key="item">{{ item }}</li>
          </ul>
          <router-link class="product-detail-cta" to="/contact?tab=quote">Request a sample</router-link>
        </div>
      </div>
    </section>

    <section class="section-spacing dark-panel">
      <div class="container reveal">
        <div class="section-title section-title--light product-detail-title">
          <p>{{ $t('caseDetail.specification') }}</p>
          <h2>Technical notes for this product</h2>
        </div>
        <div class="spec-grid">
          <article v-for="item in product.specs" :key="item.label" class="spec-card">
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </article>
        </div>
      </div>
    </section>

    <section class="section-spacing light-panel">
      <div class="container grid-two reveal">
        <div class="section-title">
          <p>{{ $t('common.surfaceOptions') }}</p>
          <h2>How the same format can change character</h2>
        </div>
        <ul class="surface-list">
          <li v-for="item in product.surfaces" :key="item">{{ item }}</li>
        </ul>
      </div>
    </section>

    <section class="section-spacing light-panel">
      <div class="container reveal">
        <div class="construction-grid">
          <article v-for="item in product.construction" :key="item.title">
            <h3>{{ item.title }}</h3>
            <p>{{ item.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section-spacing dark-panel">
      <div class="container reveal">
        <div class="section-title section-title--light product-detail-title">
          <p>Product gallery</p>
          <h2>Material, structure, and project references</h2>
        </div>
        <div class="gallery-grid">
          <div v-for="image in product.gallery" :key="image" class="gallery-grid__item">
            <img :src="image" :alt="product.title" loading="lazy">
          </div>
        </div>
      </div>
    </section>

    <section class="section-spacing light-panel">
      <div class="container grid-two reveal">
        <div class="section-title">
          <p>Typical use</p>
          <h2>Projects this format suits well</h2>
        </div>
        <div>
          <ul class="detail-list detail-list--dark">
            <li v-for="item in product.applications" :key="item">{{ item }}</li>
          </ul>
          <router-link class="product-detail-cta product-detail-cta--dark" to="/products">{{ $t('common.backToAllProducts') }}</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'

export default {
  name: 'ProductDetailPage',
  components: {
    PageTrail
  },
  computed: {
    product () {
      return site.productDetails[this.$route.params.slug] || null
    }
  },
  watch: {
    '$route.params.slug' () {
      if (!this.product) {
        this.$router.replace('/products')
      }
    }
  },
  mounted () {
    if (!this.product) {
      this.$router.replace('/products')
    }
  }
}
</script>

<style>
.detail-list {
  margin: 0;
  padding-left: 22px;
  font-size: 16px;
  line-height: 1.9;
  color: rgba(23, 20, 17, 0.82);
}

.detail-list--dark {
  color: rgba(23, 20, 17, 0.82);
}

.detail-list li + li {
  margin-top: 10px;
}

.product-detail-title {
  margin-bottom: 32px;
}

.product-detail-cta {
  min-width: 150px;
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 28px;
  padding: 0 20px;
  border: 1px solid rgba(23, 20, 17, 0.18);
  background: rgba(255, 255, 255, 0.4);
}

.product-detail-cta--dark {
  background: #171411;
  color: #f5f3ef;
}

.spec-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.spec-card {
  padding: 26px;
  background: #fff;
  color: var(--dark-text);
  border: 1px solid rgba(23, 20, 17, 0.06);
}

.spec-card span {
  display: block;
  margin-bottom: 12px;
  font-size: 14px;
  color: rgba(23, 20, 17, 0.54);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.spec-card strong {
  font-size: 19px;
  font-weight: 400;
  line-height: 1.5;
}

.surface-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.surface-list li {
  padding: 20px;
  border: 1px solid rgba(23, 20, 17, 0.12);
  background: #fff;
  line-height: 1.8;
}

.construction-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}

.construction-grid article {
  padding: 28px;
  border: 1px solid rgba(23, 20, 17, 0.12);
  background: #fff;
}

.construction-grid h3 {
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 400;
}

.construction-grid p {
  margin: 0;
  line-height: 1.8;
  color: rgba(23, 20, 17, 0.76);
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.gallery-grid__item {
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 20px 42px rgba(0, 0, 0, 0.16);
}

.gallery-grid__item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.gallery-grid__item:hover img {
  transform: scale(1.04);
}

@media (max-width: 1100px) {
  .spec-grid,
  .surface-list,
  .construction-grid,
  .gallery-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .detail-list {
    padding-left: 18px;
    font-size: 15px;
    line-height: 1.75;
  }

  .product-detail-title {
    margin-bottom: 24px;
  }

  .product-detail-cta {
    width: 100%;
  }

  .spec-card,
  .surface-list li,
  .construction-grid article {
    padding: 20px;
  }

  .spec-card strong {
    font-size: 17px;
  }

  .construction-grid h3 {
    font-size: 20px;
  }

  .gallery-grid {
    gap: 14px;
  }
}
</style>
